/**
 * MessageInfoDrawer — "Dados da mensagem" (padrão WhatsApp).
 *
 * O dono de uma mensagem vê quem recebeu/leu. Exibe a prévia da mensagem
 * (corpo + hora) no topo e 3 seções empilhadas, cada uma com ícone + contagem +
 * lista (avatar com iniciais + nome), na ordem:
 *   - ✓✓ (azul)  Lido por (N)
 *   - ✓✓ (cinza) Recebido, não lido (N)
 *   - ✓  (cinza) Não recebido (N)
 * Seção sem destinatários é ocultada. Estados de carregamento e erro discretos.
 *
 * Reusa o padrão de avatar do chat (Mantine Avatar com iniciais, color="teal",
 * variant="filled", radius="xl") — o mesmo do MembersDrawer/HumanBubble.
 */
import { Avatar, Box, Drawer, Group, Loader, Paper, Stack, Text } from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import { IconCheck, IconChecks } from '@tabler/icons-react'
import type { ChatMessage, MessageReceiptUser } from '@/api/chat'
import { useMessageInfo } from '@/hooks/useChat'

function formatTime(iso: string): string {
  const d = new Date(iso)
  return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
}

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/)
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

function displayName(u: MessageReceiptUser): string {
  return u.full_name?.trim() || u.email
}

interface SectionProps {
  icon: React.ReactNode
  title: string
  users: MessageReceiptUser[]
}

function Section({ icon, title, users }: SectionProps) {
  if (users.length === 0) return null
  return (
    <Stack gap="xs">
      <Group gap={6} wrap="nowrap" align="center">
        <Box style={{ display: 'flex', alignItems: 'center' }}>{icon}</Box>
        <Text size="sm" fw={600}>
          {title}
        </Text>
        <Text size="sm" c="dimmed">
          ({users.length})
        </Text>
      </Group>
      <Stack gap={4}>
        {users.map((u) => {
          const name = displayName(u)
          return (
            <Group key={u.user_id} gap="sm" wrap="nowrap" px="xs" py={4}>
              <Avatar
                size="sm"
                radius="xl"
                color="teal"
                variant="filled"
                style={{ flexShrink: 0 }}
              >
                {getInitials(name)}
              </Avatar>
              <Box style={{ flex: 1, minWidth: 0 }}>
                <Text size="sm" fw={500} lineClamp={1}>
                  {name}
                </Text>
              </Box>
            </Group>
          )
        })}
      </Stack>
    </Stack>
  )
}

interface Props {
  opened: boolean
  onClose: () => void
  cid: number
  message: ChatMessage
}

export function MessageInfoDrawer({ opened, onClose, cid, message }: Props) {
  const isMobile = useMediaQuery('(max-width: 768px)')
  const { data, isLoading, isError } = useMessageInfo(cid, message.id, opened)

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position={isMobile ? 'bottom' : 'right'}
      size={isMobile ? '70%' : 360}
      title={
        <Text fw={600} size="sm">
          Dados da mensagem
        </Text>
      }
      styles={{
        header: {
          borderBottom: '1px solid var(--ai-border-light, var(--mantine-color-gray-2))',
          paddingBottom: 'var(--mantine-spacing-sm)',
        },
      }}
    >
      <Stack gap="lg" pt="sm">
        {/* Prévia da mensagem */}
        <Paper
          px="md"
          py="sm"
          withBorder
          style={{
            backgroundColor: 'var(--mantine-color-teal-1)',
            borderRight: '3px solid var(--mantine-color-teal-5)',
          }}
        >
          <Text
            size="sm"
            style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}
          >
            {message.body ?? ''}
          </Text>
          <Text size="xs" c="dimmed" mt={4} ta="right">
            {formatTime(message.created_at)}
          </Text>
        </Paper>

        {isLoading && (
          <Group justify="center" py="xl">
            <Loader size="sm" type="dots" color="teal" />
            <Text size="sm" c="dimmed">
              Carregando…
            </Text>
          </Group>
        )}

        {!isLoading && isError && (
          <Text size="sm" c="dimmed" ta="center" py="xl">
            Não foi possível carregar os dados da mensagem.
          </Text>
        )}

        {!isLoading && !isError && data && (
          <Stack gap="lg">
            <Section
              icon={<IconChecks size={16} color="var(--mantine-color-blue-5)" />}
              title="Lido por"
              users={data.read}
            />
            <Section
              icon={<IconChecks size={16} color="var(--mantine-color-gray-5)" />}
              title="Recebido, não lido"
              users={data.delivered_not_read}
            />
            <Section
              icon={<IconCheck size={16} color="var(--mantine-color-gray-5)" />}
              title="Não recebido"
              users={data.not_delivered}
            />
          </Stack>
        )}
      </Stack>
    </Drawer>
  )
}
